import { useCallback, useEffect, useRef, useState } from "react";
import { DEFAULT_PAGE_SIZE, unwrapPaged } from "./paging";


const usePagedList = ({
  fetcher,
  pageSize = DEFAULT_PAGE_SIZE,
  enabled = true,
  debounceMs = 300,
}) => {
  const [rows, setRows] = useState([]);
  const [paging, setPaging] = useState({});
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [queryString, setQueryString] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  // Guards against a slow page-1 response landing after a fast page-2 one.
  const requestId = useRef(0);

  useEffect(() => {
    if (!enabled) return undefined;

    const currentRequest = ++requestId.current;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetcher({ pageNumber, pageSize, queryString });
        if (currentRequest !== requestId.current) return;
        const { rows: nextRows, paging: nextPaging } = unwrapPaged(res, pageSize);
        setRows(nextRows);
        setPaging(nextPaging);
      } catch (error) {
        if (currentRequest !== requestId.current) return;
        console.error(error);
        setRows([]);
        setPaging({});
      } finally {
        if (currentRequest === requestId.current) setLoading(false);
      }
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [fetcher, pageNumber, pageSize, queryString, reloadKey, enabled, debounceMs]);

  const onPageChange = useCallback((page) => setPageNumber(page), []);

  const onSearch = useCallback((value) => {
    setQueryString(value);
    setPageNumber(1);
  }, []);

  const refresh = useCallback(() => setReloadKey((key) => key + 1), []);

  return {
    rows,
    setRows,
    paging,
    loading,
    pageNumber,
    queryString,
    onPageChange,
    onSearch,
    refresh,
  };
};

export default usePagedList;
